/**
 * ISO 8601 期間（duration）字串 <-> Minutes 的雙向轉換。
 *
 * MSPDI 的 `<Duration>` / `<Work>` 等欄位使用 xsd:duration 格式，
 * 例：`PT8H0M0S`（8 小時）、`PT40H0M0S`（一週 40 工時）、`P1D`（少見，1 天）。
 * 匯出時一律固定產生 `PT#H#M#S` 形式，符合 MS Project 實際輸出慣例。
 */

import { minutes, rawNumber, type Minutes } from '../../domain/branded.js';
import type { DurationConversion } from '../../domain/calendar.js';

const ISO_DURATION_RE =
  /^(-)?P(?:(\d+(?:\.\d+)?)Y)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)W)?(?:(\d+(?:\.\d+)?)D)?(?:T(?:(\d+(?:\.\d+)?)H)?(?:(\d+(?:\.\d+)?)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

/** 預設換算：8 小時／日、40 小時／週、20 工作天／月（MS Project 內建預設）。 */
export const DEFAULT_DURATION_CONVERSION: DurationConversion = {
  minutesPerDay: minutes(480),
  minutesPerWeek: minutes(2400),
  daysPerMonth: 20,
};

/**
 * 解析 ISO 8601 期間字串為 Minutes。
 * 無法解析時回傳 null（由呼叫端決定如何處理 fallback 與 Diagnostic）。
 */
export function parseIsoDuration(
  value: string,
  conversion: DurationConversion = DEFAULT_DURATION_CONVERSION,
): Minutes | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return null;
  }
  const match = ISO_DURATION_RE.exec(trimmed);
  if (!match || (trimmed === 'P' || trimmed === '-P')) {
    return null;
  }
  const [, sign, y, mo, w, d, h, mi, s] = match;
  const minutesPerDay = rawNumber(conversion.minutesPerDay);
  const minutesPerWeek = rawNumber(conversion.minutesPerWeek);
  const minutesPerMonth = conversion.daysPerMonth * minutesPerDay;
  const minutesPerYear = 12 * minutesPerMonth;

  const total =
    (y !== undefined ? Number(y) * minutesPerYear : 0) +
    (mo !== undefined ? Number(mo) * minutesPerMonth : 0) +
    (w !== undefined ? Number(w) * minutesPerWeek : 0) +
    (d !== undefined ? Number(d) * minutesPerDay : 0) +
    (h !== undefined ? Number(h) * 60 : 0) +
    (mi !== undefined ? Number(mi) : 0) +
    (s !== undefined ? Number(s) / 60 : 0);

  if (
    y === undefined &&
    mo === undefined &&
    w === undefined &&
    d === undefined &&
    h === undefined &&
    mi === undefined &&
    s === undefined
  ) {
    return null;
  }

  // 正規化到整秒精度（1/60 分鐘）：`formatIsoDuration` 序列化時一律四捨五入到整秒，
  // 若解析端保留任意小數秒精度，`formatIsoDuration(parseIsoDuration(x))` 對某些輸入
  // 就不是 idempotent，`parseIsoDuration(formatIsoDuration(m))` 也無法精確還原 m。
  // 兩端統一在「整秒」這個精度上一致，才能保證匯出後再匯入不會漂移。
  const rounded = Math.round(total * 60) / 60;
  return minutes(sign === '-' ? -rounded : rounded);
}

/** 將 Minutes 序列化為 MSPDI 慣用的 `PT#H#M#S` 格式。 */
export function formatIsoDuration(value: Minutes): string {
  const raw = rawNumber(value);
  const negative = raw < 0;
  const totalSeconds = Math.round(Math.abs(raw) * 60);
  const hours = Math.floor(totalSeconds / 3600);
  const mins = Math.floor((totalSeconds % 3600) / 60);
  const secs = totalSeconds % 60;
  return `${negative ? '-' : ''}PT${hours}H${mins}M${secs}S`;
}
